/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	IModelNodeInfo,
	ISchemaConstArrayModel,
	isSchemaConstArrayModel,
	isSchemaRecursiveModel,
	isSchemaValueModel,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { INSPECTOR_NODE_TYPE, TInspectorNode } from "../InspectorNodeTypes";
import { mapModelToNodes } from "../index";
import { isFunction } from "@adapptio/lib-shared";
import { IInspectorControl_Add, INSPECTOR_CONTROL_TYPE } from "../controls";
import { createDefaultNode } from "../createDefaultNode";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";
import { getParentComponentName } from "../utils";

export function constArrayMapper(model: ISchemaConstArrayModel<TGenericBlueprintSchema>): TInspectorNode {
	if (!isSchemaConstArrayModel(model)) {
		return;
	}

	const controlAdd: IInspectorControl_Add = {
		key: String(model.nodeId),
		type: INSPECTOR_CONTROL_TYPE.ADD,
		model
	};

	const deps = [ model ];

	return {
		type: INSPECTOR_NODE_TYPE.LIST,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		default: model.schema.opts.default || null,
		options: model.schema.opts["options"],
		required: model.schema.opts.constraints?.required || false,
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		deps: deps,
		model: model,
		controls: [ controlAdd ],
		getChildren: (childModel, index) => () => {
			const childNode = { ...mapModelToNodes(childModel) };

			const getModelNodeInfo = model.schema.opts.getElementModelNodeInfo;

			const modelNodeInfo: IModelNodeInfo = isFunction(getModelNodeInfo)
				? getModelNodeInfo(childModel, index)
				: {
						icon: "",
						label: ""
				  };

			const itemLabel = modelNodeInfo.label || childNode.label || `#${index + 1}`;

			const itemNode = createDefaultNode({
				key: `${childModel.nodeId}`,
				label: itemLabel,
				deps: [ ...childNode.deps ],
				children: [ () => childNode ]
			});

			if (
				isSchemaValueModel(childModel) ||
				(isSchemaRecursiveModel(childModel) && isSchemaValueModel(childModel.value))
			) {
				for (let i = childNode.controls.length; i > -1; i--) {
					const control = childNode.controls[i];

					if (control?.type === INSPECTOR_CONTROL_TYPE.VALUE_TYPE) {
						itemNode.controls.push(...childNode.controls.splice(i, 1));
						break;
					}
				}
			}

			return itemNode;
		}
	};
}
